/**
 * Pure utility functions for ContentLibrary — no React Native imports.
 * Safe to import in Jest without requiring native module mocks.
 */
import { ContentLibraryItem } from "./adapters"

export type SortKey = "latest" | "sidekick" | "topic" | "type"

/**
 * Apply the active sort + sub-filter to an items array.
 */
export function sortAndFilterItems(
  items: ContentLibraryItem[],
  sort: SortKey,
  sidekick: string | null,
  topic: string | null,
  type: string | null,
): ContentLibraryItem[] {
  let result = [...items]

  // Filters are applied independently of the active sort so that switching sorts
  // doesn't silently discard an active filter value.
  if (sidekick !== null) {
    result = result.filter((i) => i.sidekickName === sidekick)
  }
  if (topic !== null) {
    result = result.filter((i) => i.topic === topic)
  }
  if (type !== null) {
    result = result.filter((i) => i.mediaType === type)
  }

  switch (sort) {
    case "latest":
      result.sort((a, b) => {
        const aDate = a.lastPublishedAt ?? a.createdAt
        const bDate = b.lastPublishedAt ?? b.createdAt
        if (aDate && bDate) {
          return new Date(bDate).getTime() - new Date(aDate).getTime()
        }
        return (b.priority ?? 0) - (a.priority ?? 0)
      })
      break
    case "sidekick":
      result.sort((a, b) => a.sidekickName.localeCompare(b.sidekickName))
      break
    case "topic":
      result.sort((a, b) => (a.topic ?? "").localeCompare(b.topic ?? ""))
      break
    case "type":
      result.sort((a, b) => (a.mediaType ?? "").localeCompare(b.mediaType ?? ""))
      break
  }

  return result
}

/**
 * Filter items by title substring match (case-insensitive).
 */
export function searchItems(items: ContentLibraryItem[], query: string): ContentLibraryItem[] {
  if (!query.trim()) return items
  const q = query.toLowerCase()
  return items.filter((i) => i.title.toLowerCase().includes(q))
}
